import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  ContentCopy as FileCopyIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { Channel } from 'types/apis/cxEditor';
import { PermissionGate } from 'components/access/PermissionGate';
import { CAPABILITIES } from 'framework/access/capabilities';

export interface CopiedToTemplate {
  toTemplateKey: string;
  toChannel: string;
  copiedAt: string;
  copiedBy: string;
  isInSync?: boolean;
  /** Value in the target template before the last copy was applied. */
  originalValue?: string;
  /** Value that was actually written to the target template during the last copy. */
  lastCopiedValue?: string;
}

export interface TargetTemplateInfo {
  notificationType: string;
  displayName: string;
  templateKey: string;
  channel: Channel;
  /** Fuel type for this template variant (ELECTRIC, GAS, WATER, etc.) — empty string for no-fuel templates. */
  fuelType: string;
  /** Configs/strings (by configKey) whose registry-declared section is owned
   *  by this template. The dialog shows only these per-template, and copy
   *  obeys the user's per-template selection. */
  applicableConfigKeys: string[];
  /** Current value of each config/string in this target template (from backend templateDiffs[].value).
   *  Shown in the tooltip so the user can see what will be overwritten. */
  values?: Record<string, string>;
}

export interface EditedConfigInfo {
  /** configKey for configs, stringResourceId for string resources */
  configKey: string;
  /** configType for configs, empty for string resources */
  configType: string;
  displayName: string;
  newValue: string;
  fieldType: 'text' | 'image' | 'color' | 'metadata' | 'string';
  /** Source config metadata — carried directly from the API response so the copy
   *  payload can be built without ref lookups or hardcoded fallbacks. */
  configDataType?: string;
  configRegex?: string;
  component?: string;
  documentation?: string;
  /** For string resources: locale from API response, avoids local-state lookup during copy */
  locale?: string;
  /** Templates this config has previously been copied to (from API) */
  copiedToTemplates?: CopiedToTemplate[];
  /** Raw dot-notation section paths from the registry (e.g. "EMAIL.ALL.FOOTER").
   *  Used to derive which channels (Email/HER/Web) this config/string applies to. */
  registrySections?: string[];
  /** Registry `component` values for this key (e.g. EMAIL, HER, DASHBOARD).
   *  Used to gate which template-channel sections are applicable. */
  registryComponents?: string[];
  /** Value before the user's edit — used to populate source values in the copy dialog. */
  originalValue?: string;
}

/** Per-target selection passed to onConfirm: each target carries its own
 *  user-confirmed config subset (a strict subset of applicableConfigKeys). */
export interface TargetWithConfigs {
  target: TargetTemplateInfo;
  configKeys: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (selections: TargetWithConfigs[]) => Promise<void>;
  targetTemplates: TargetTemplateInfo[];
  editedConfigs: EditedConfigInfo[];
  isCopying?: boolean;
  /** Default fuel type to filter by — from the source template. Empty string means no-fuel. */
  sourceFuelType?: string;
  /** Human-readable name of the source section being exported. */
  sourceSection?: string;
}

function truncateValue(value: string, max = 70): string {
  if (!value) return '(empty)';
  const clean = value.replace(/<[^>]*>/g, '').trim();
  return clean.length > max ? clean.slice(0, max) + '…' : clean || '(empty)';
}

/** Group raw registry section paths by channel → section-name list. */
function groupSectionsByChannel(rawSections: string[]): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};
  for (const path of rawSections) {
    const parts = path.split('.');
    const prefix = parts[0].toUpperCase();
    const channel =
      prefix === 'EMAIL' ? 'Email' :
      prefix === 'HER' ? 'HER' :
      prefix === 'WEB' || prefix === 'DASHBOARD' ? 'Web' :
      prefix === 'SMS' ? 'SMS' : prefix;
    const sectionName = parts[parts.length - 1];
    if (!grouped[channel]) grouped[channel] = [];
    if (!grouped[channel].includes(sectionName)) grouped[channel].push(sectionName);
  }
  return grouped;
}

function requiredComponentForChannel(channel: Channel): string {
  return channel === 'Email'
    ? 'EMAIL'
    : channel === 'HER'
      ? 'HER'
      : channel === 'Web'
        ? 'DASHBOARD'
        : channel === 'SMS'
          ? 'SMS'
          : '';
}

function componentFromSectionPrefix(prefix: string): string | null {
  const p = prefix.toUpperCase();
  if (p === 'EMAIL') return 'EMAIL';
  if (p === 'HER') return 'HER';
  if (p === 'WEB' || p === 'DASHBOARD') return 'DASHBOARD';
  if (p === 'SMS') return 'SMS';
  return null;
}

function fieldTypeLabel(type: EditedConfigInfo['fieldType']): string {
  return type === 'string' ? 'String' : 'Config';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

const CHANNEL_DISPLAY: Record<Channel, string> = {
  Email: 'Email',
  Web: 'Web',
  HER: 'HER',
  SMS: 'SMS',
};

export const CopyToEmailsDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  targetTemplates,
  editedConfigs,
  isCopying = false,
  sourceFuelType = '',
  sourceSection,
}) => {
  // Single source of truth: per-template selected config keys.
  //   key absent  → template not selected (won't be copied to)
  //   key present → template selected; the Set holds the chosen configKeys
  const [templateConfigSelections, setTemplateConfigSelections] = React.useState<
    Map<string, Set<string>>
  >(new Map());

  // Track expanded accordions independently from selection so toggling a
  // template's checkbox does not collapse it.
  const [expandedTemplates, setExpandedTemplates] = React.useState<Set<string>>(new Set());

  // Fuel type filter — defaults to the source template's fuel type on open.
  // '' represents no-fuel / fuel-agnostic templates.
  const [fuelFilter, setFuelFilter] = React.useState<string>(sourceFuelType);

  React.useEffect(() => {
    if (open) setFuelFilter(sourceFuelType);
  }, [open, sourceFuelType]);

  React.useEffect(() => {
    if (!open) return;

    const initial = new Map<string, Set<string>>();
    for (const tmpl of targetTemplates) {
      const applicable = tmpl.applicableConfigKeys || [];
      if (applicable.length === 0) continue;

      // Pre-select only configs that haven't been copied to this template yet.
      // Fully synced templates get nothing pre-selected (user opts back in).
      // Partially synced templates get only the un-copied subset pre-selected.
      const notYetCopied = applicable.filter(key => {
        const cfg = editedConfigs.find(c => c.configKey === key);
        return !cfg?.copiedToTemplates?.some(ct => ct.toTemplateKey === tmpl.templateKey);
      });

      if (notYetCopied.length > 0) {
        initial.set(tmpl.templateKey, new Set(notYetCopied));
      }
    }
    setTemplateConfigSelections(initial);
    setExpandedTemplates(new Set());
  }, [open, targetTemplates, editedConfigs]);

  const toggleTemplate = (templateKey: string) => {
    setTemplateConfigSelections(prev => {
      const next = new Map(prev);
      if (next.has(templateKey)) {
        next.delete(templateKey);
      } else {
        const tmpl = targetTemplates.find(t => t.templateKey === templateKey);
        if (tmpl) next.set(templateKey, new Set(tmpl.applicableConfigKeys));
      }
      return next;
    });
  };

  const toggleConfig = (templateKey: string, configKey: string) => {
    setTemplateConfigSelections(prev => {
      const next = new Map(prev);
      const cfgSet = new Set(next.get(templateKey) ?? []);
      if (cfgSet.has(configKey)) {
        cfgSet.delete(configKey);
      } else {
        cfgSet.add(configKey);
      }
      if (cfgSet.size === 0) {
        next.delete(templateKey);
      } else {
        next.set(templateKey, cfgSet);
      }
      return next;
    });
  };

  const toggleExpanded = (templateKey: string) => {
    setExpandedTemplates(prev => {
      const next = new Set(prev);
      if (next.has(templateKey)) next.delete(templateKey); else next.add(templateKey);
      return next;
    });
  };

  const selectedTemplateCount = templateConfigSelections.size;

  const canCopy = selectedTemplateCount > 0 &&
    Array.from(templateConfigSelections.values()).some(s => s.size > 0);

  const handleConfirm = async () => {
    const selections: TargetWithConfigs[] = [];
    for (const tmpl of targetTemplates) {
      const cfgSet = templateConfigSelections.get(tmpl.templateKey);
      if (cfgSet && cfgSet.size > 0) {
        selections.push({ target: tmpl, configKeys: Array.from(cfgSet) });
      }
    }
    await onConfirm(selections);
  };

  // Derive available fuel options from the full template list so the toggle
  // only shows fuels that actually exist among the candidates.
  const availableFuels = React.useMemo(() => {
    const fuels = new Set<string>();
    for (const t of targetTemplates) fuels.add(t.fuelType.toUpperCase());
    return Array.from(fuels).sort();
  }, [targetTemplates]);

  // Group targets by channel, filtered to the selected fuel type.
  const channelOrder: Channel[] = ['Email', 'Web', 'HER', 'SMS'];
  const templatesByChannel = channelOrder
    .map(ch => ({
      channel: ch,
      templates: targetTemplates.filter(
        t => t.channel === ch && t.fuelType.toUpperCase() === fuelFilter.toUpperCase(),
      ),
    }))
    .filter(g => g.templates.length > 0);

  const editedConfigByKey = React.useMemo(() => {
    const m = new Map<string, EditedConfigInfo>();
    for (const c of editedConfigs) m.set(c.configKey, c);
    return m;
  }, [editedConfigs]);

  const visibleTemplates = templatesByChannel.flatMap(g => g.templates);

  const handleFuelChange = (_e: React.MouseEvent, val: string | null) => {
    if (val === null) return;
    setFuelFilter(val);
    const next = new Map<string, Set<string>>();
    for (const tmpl of targetTemplates) {
      if (tmpl.fuelType.toUpperCase() !== val.toUpperCase()) continue;
      const applicable = tmpl.applicableConfigKeys || [];
      if (applicable.length === 0) continue;
      const notYetCopied = applicable.filter(key => {
        const cfg = editedConfigByKey.get(key);
        return !cfg?.copiedToTemplates?.some(ct => ct.toTemplateKey === tmpl.templateKey);
      });
      if (notYetCopied.length > 0) {
        next.set(tmpl.templateKey, new Set(notYetCopied));
      }
    }
    setTemplateConfigSelections(next);
  };

  const selectAll = () => {
    setTemplateConfigSelections(prev => {
      const next = new Map(prev);
      for (const tmpl of visibleTemplates) {
        const applicable = tmpl.applicableConfigKeys || [];
        if (applicable.length > 0) next.set(tmpl.templateKey, new Set(applicable));
      }
      return next;
    });
  };

  const deselectAll = () => {
    setTemplateConfigSelections(prev => {
      const next = new Map(prev);
      for (const tmpl of visibleTemplates) next.delete(tmpl.templateKey);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={isCopying ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileCopyIcon fontSize="small" />
          <Typography variant="h6" component="span">Copy {sourceSection || 'Section'} to Other Templates</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each template lists only the configurations and strings that belong to a section the
          template owns. Expand a template to control its individual selections.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {availableFuels.length > 1 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Fuel:
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={fuelFilter.toUpperCase()}
                onChange={handleFuelChange}
              >
                {availableFuels.map(fuel => (
                  <ToggleButton key={fuel} value={fuel} sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem', textTransform: 'none' }}>
                    {fuel || 'DF/CDF'}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          ) : <Box />}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" onClick={selectAll} sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}>
              Select All
            </Button>
            <Button size="small" onClick={deselectAll} sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}>
              Deselect All
            </Button>
          </Box>
        </Box>

        {targetTemplates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No other templates own a section that contains the edited configurations.
          </Typography>
        ) : templatesByChannel.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No templates match the selected fuel type. Switch the fuel filter above to see other variants.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {templatesByChannel.map(({ channel, templates }) => (
              <Box key={channel}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', px: 0.5, mb: 0.5, display: 'block' }}
                >
                  {CHANNEL_DISPLAY[channel]}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {templates.map(tmpl => {
                    const applicable = tmpl.applicableConfigKeys || [];
                    const selectedSet = templateConfigSelections.get(tmpl.templateKey);
                    const selectedCount = selectedSet?.size ?? 0;
                    const isChecked = selectedCount > 0;
                    const isIndeterminate = selectedCount > 0 && selectedCount < applicable.length;
                    const isExpanded = expandedTemplates.has(tmpl.templateKey);

                    const syncedCount = applicable.filter(key => {
                      const cfg = editedConfigByKey.get(key);
                      return !!cfg?.copiedToTemplates?.some(ct => ct.toTemplateKey === tmpl.templateKey && ct.isInSync);
                    }).length;
                    const isFullySynced = applicable.length > 0 && syncedCount === applicable.length;
                    const isPartiallySynced = syncedCount > 0 && !isFullySynced;

                    return (
                      <Accordion
                        key={tmpl.templateKey}
                        expanded={isExpanded}
                        onChange={() => toggleExpanded(tmpl.templateKey)}
                        disableGutters
                        elevation={0}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ minHeight: 0, '& .MuiAccordionSummary-content': { my: 0.75 } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              indeterminate={isIndeterminate}
                              onChange={() => toggleTemplate(tmpl.templateKey)}
                              onClick={e => e.stopPropagation()}
                              sx={{ p: 0.5 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {tmpl.displayName}
                                </Typography>
                                <Chip
                                  label={`${selectedCount}/${applicable.length} configs`}
                                  size="small"
                                  variant="outlined"
                                  color={selectedCount === applicable.length ? 'primary' : 'default'}
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                {isFullySynced && (
                                  <Tooltip
                                    arrow
                                    title="All selected configurations have already been copied here. Copying again will overwrite with current values."
                                  >
                                    <Chip
                                      label="All synced"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem', cursor: 'help' }}
                                      onClick={e => e.stopPropagation()}
                                    />
                                  </Tooltip>
                                )}
                                {isPartiallySynced && (
                                  <Tooltip
                                    arrow
                                    title={`${syncedCount} of ${applicable.length} configurations are in sync.`}
                                  >
                                    <Chip
                                      label={`${syncedCount}/${applicable.length} synced`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem', cursor: 'help' }}
                                      onClick={e => e.stopPropagation()}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                                {tmpl.templateKey}
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1, pl: 2 }}>
                          {applicable.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              No applicable configurations for this template.
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {applicable.map(configKey => {
                                const cfg = editedConfigByKey.get(configKey);
                                if (!cfg) return null;
                                const isSelected = !!selectedSet?.has(configKey);
                                const copyHistory = cfg.copiedToTemplates ?? [];
                                const previouslyCopied = copyHistory.some(ct => ct.toTemplateKey === tmpl.templateKey);
                                const isConfigSynced = copyHistory.some(ct => ct.toTemplateKey === tmpl.templateKey && ct.isInSync);
                                return (
                                  <Box
                                    key={configKey}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 0.5,
                                      cursor: 'pointer',
                                      '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                    onClick={() => toggleConfig(tmpl.templateKey, configKey)}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isSelected}
                                      onChange={() => toggleConfig(tmpl.templateKey, configKey)}
                                      onClick={e => e.stopPropagation()}
                                      sx={{ p: 0.25 }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" fontWeight={500}>
                                          {cfg.displayName || cfg.configKey}
                                        </Typography>
                                        <Chip
                                          label={fieldTypeLabel(cfg.fieldType)}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 16, fontSize: '0.6rem' }}
                                        />
                                        {cfg.registrySections && cfg.registrySections.length > 0 && (() => {
                                          const requiredComponent = requiredComponentForChannel(tmpl.channel);
                                          const registryComponents = cfg.registryComponents ?? [];
                                          const componentGateActive = registryComponents.length > 0;

                                          // When registry `component` metadata is present, use it to filter
                                          // out sections that belong to other channels.
                                          const rawForTooltip = componentGateActive
                                            ? (registryComponents.includes(requiredComponent)
                                              ? cfg.registrySections.filter(path => {
                                                  const prefix = path.split('.')[0] ?? '';
                                                  return componentFromSectionPrefix(prefix) === requiredComponent;
                                                })
                                              : [])
                                            : cfg.registrySections;

                                          const grouped = rawForTooltip.length > 0 ? groupSectionsByChannel(rawForTooltip) : {};
                                          const currentTargetValue = tmpl.values?.[configKey];
                                          return (
                                            <Tooltip
                                              arrow
                                              title={
                                                <Box sx={{ p: 0.25 }}>
                                                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                                                    Impacted sections:
                                                  </Typography>
                                                  {rawForTooltip.length === 0 ? (
                                                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                                      None for this template channel
                                                    </Typography>
                                                  ) : (
                                                    Object.entries(grouped).map(([ch, sections]) => (
                                                      <Typography key={ch} variant="caption" display="block" sx={{ fontSize: '0.6rem' }}>
                                                        <strong>{ch}:</strong> {sections.join(', ')}
                                                      </Typography>
                                                    ))
                                                  )}
                                                  {currentTargetValue !== undefined && (
                                                    <>
                                                      <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 0.75, mb: 0.25 }}>
                                                        Current value in target:
                                                      </Typography>
                                                      <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: 'text.secondary', wordBreak: 'break-all' }}>
                                                        {truncateValue(currentTargetValue)}
                                                      </Typography>
                                                    </>
                                                  )}
                                                </Box>
                                              }
                                              slotProps={{
                                                tooltip: {
                                                  sx: {
                                                    bgcolor: 'background.paper',
                                                    color: 'text.primary',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    boxShadow: 2,
                                                    maxWidth: 280,
                                                    fontSize: '0.75rem',
                                                    p: 1.5,
                                                  },
                                                },
                                              }}
                                            >
                                              <Box
                                                component="span"
                                                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
                                                onClick={e => e.stopPropagation()}
                                              >
                                                <InfoOutlinedIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                                              </Box>
                                            </Tooltip>
                                          );
                                        })()}
                                        {previouslyCopied && (
                                          <Tooltip
                                            arrow
                                            title={
                                              <Box sx={{ p: 0.25 }}>
                                                <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                                                  Previously copied here:
                                                </Typography>
                                                {copyHistory
                                                  .filter(ct => ct.toTemplateKey === tmpl.templateKey)
                                                  .map(ct => (
                                                    <Typography key={ct.copiedAt} variant="caption" display="block" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                                                      {formatDate(ct.copiedAt)} · {ct.copiedBy}
                                                    </Typography>
                                                  ))}
                                              </Box>
                                            }
                                            componentsProps={{
                                              tooltip: {
                                                sx: {
                                                  bgcolor: 'background.paper',
                                                  color: 'text.primary',
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  boxShadow: 2,
                                                  maxWidth: 280,
                                                  fontSize: '0.75rem',
                                                  p: 1.5,
                                                },
                                              },
                                            }}
                                          >
                                            <Chip
                                              label={isConfigSynced ? 'Synced' : 'Copied'}
                                              size="small"
                                              color={isConfigSynced ? 'success' : 'info'}
                                              variant="outlined"
                                              sx={{ height: 16, fontSize: '0.6rem', cursor: 'help' }}
                                              onClick={e => e.stopPropagation()}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                        {truncateValue(cfg.newValue)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={isCopying} size="small">
          Cancel
        </Button>
        <PermissionGate require={CAPABILITIES.WYSIWYG_COPY} mode="disable">
          <Button
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={isCopying || !canCopy}
            startIcon={isCopying ? <CircularProgress size={14} color="inherit" /> : <FileCopyIcon />}
          >
            {isCopying
              ? 'Copying…'
              : `Copy to ${selectedTemplateCount} template${selectedTemplateCount !== 1 ? 's' : ''}`}
          </Button>
        </PermissionGate>
      </DialogActions>
    </Dialog>
  );
};

export default CopyToEmailsDialog;
